/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
/* Copied from Backstage repo plugins\catalog-react\src\components\UserListPicker\useOwnedEntitiesCount.ts and customized */

import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  EntityOwnerFilter,
  EntityUserFilter,
  catalogApiRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { compact, intersection } from 'lodash';
import { useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';
import {
  CatalogFilters,
  reduceCatalogFilters,
} from '../../../../utils/filters';

export function useOwnedEntitiesCount() {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { filters } = useEntityList();

  const { value: ownershipEntityRefs, loading: loadingEntityRefs } = useAsync(
    async () => (await identityApi.getBackstageIdentity()).ownershipEntityRefs,
    // load only on mount
    [],
  );

  const { user, owners, ...allFilters } = filters;
  const catalogFilters = reduceCatalogFilters(
    compact(Object.values(allFilters)),
  );

  const [{ value: count, loading: loadingEntityOwnership }, fetchEntities] =
    useAsyncFn(
      async (req: {
        ownershipEntityRefs: string[];
        owners: EntityOwnerFilter | undefined;
        filter: CatalogFilters;
      }) => {
        const ownedClaims = getOwnedCountClaims(
          req.owners,
          req.ownershipEntityRefs,
        );
        if (ownedClaims === undefined) {
          // this implicitly means that there aren't claims in common with
          // the logged in users, so avoid invoking the queryEntities endpoint
          // which will implicitly returns 0
          return 0;
        }

        const { ['metadata.name']: metadata, ...filter } = req.filter.filter;

        const { totalItems } = await catalogApi.queryEntities({
          ...req.filter,
          filter: {
            ...filter,
            'relations.ownedBy': ownedClaims,
          },
          limit: 0,
        });
        return totalItems;
      },
      [],
      { loading: true },
    );

  useDeepCompareEffect(() => {
    // context contains no filter, wait
    if (Object.keys(catalogFilters.filter).length === 0) {
      return;
    }
    // ownershipEntityRefs is loading, wait
    if (ownershipEntityRefs === undefined) {
      return;
    }
    fetchEntities({
      ownershipEntityRefs,
      owners,
      filter: catalogFilters,
    });
  }, [ownershipEntityRefs, owners, catalogFilters]);

  const loading = loadingEntityRefs || loadingEntityOwnership;

  return {
    count,
    loading,
    filter: useMemo(
      () => EntityUserFilter.owned(ownershipEntityRefs ?? []),
      [ownershipEntityRefs],
    ),
    ownershipEntityRefs,
  };
}

function getOwnedCountClaims(
  owners: EntityOwnerFilter | undefined,
  ownershipEntityRefs: string[] | undefined,
) {
  if (ownershipEntityRefs === undefined) {
    return undefined;
  }
  const ownersRefs = owners?.values ?? [];
  if (ownersRefs.length) {
    const commonOwnedBy = intersection(ownersRefs, ownershipEntityRefs);
    if (commonOwnedBy.length === 0) {
      return undefined;
    }
    return commonOwnedBy;
  }
  return ownershipEntityRefs;
}
